import {
  setDoc,
  doc,
  collection,
  getDoc,
  query,
  where,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { db, storage } from "../../../firebase";
import { uploadDocument } from "../functions/UploadDocument";
import {
  datapointConflictResolver,
  createStructuredDocument,
  reformatDocument,
  extractDatapointsAI,
  generateAuditRiskProfile,
  datapointConflictResolverSimple,
  reformatOCRDocument,
  createStructuredOCRDocument,
  createStructuredDocumentFromImage,
  reformatCSVDocument,
  reasoningConflictResolverPrompt,
  createStructuredCSVDocument,
  createStructuredWordDocument,
} from "../functions/AICalls";
import { logTokenUsage } from "../functions/AITokenUsage";
import { deleteObject, ref, listAll, getBytes } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { heroAiCallable } from "./heroAiCallable";

// Initialize Firebase Functions
const functions = getFunctions();
// Helper function to check if all required files are uploaded
const OpenAIReasoningUpdate = httpsCallable(
  functions,
  "OpenAIReasoningUpdated",
  {
    timeout: 420000,
  }
);

export const areAllRequiredFilesUploaded = (currentSection, uploadedFiles) => {
  // Check if every required file field has an uploaded file
  return currentSection?.fields.every((field) => {
    if (field.type === "file" && field.required) {
      return uploadedFiles[field.name] !== undefined;
    }
    return true; // If the field is not a required file, it is considered valid
  });
};

// building the step functions to individually handle each step in the document upload and analysis process
// --------------------------------STEP 1---------------------------------
// HELPER: Upload Document to firebase
export const handleStep1UploadDocuments = async (
  uploadedFiles,
  company,
  collectionBrand,
  systemName,
  uniqueId,
  setLoadingLabel,
  totalDocuments
) => {
  const uploadResults = {};

  try {
    let docIndex = 0;
    const percentagePerDoc = 20 / totalDocuments;

    for (const [fileName, { file, label }] of Object.entries(uploadedFiles)) {
      docIndex++;
      const fileType = uploadedFiles[fileName].fileType;

      const currentPercent = (percentagePerDoc * docIndex).toFixed(2);
      setLoadingLabel(
        `${currentPercent}% ... Uploading ${fileName} to your Storage Container`
      );
      const { filePath, downloadURL } = await uploadDocumentToFirebase(
        fileType,
        file,
        company,
        collectionBrand,
        systemName,
        uniqueId,
        fileName,
        setLoadingLabel
      );

      // Save the file path and download URL for each uploaded document
      uploadResults[fileName] = {
        filePath,
        downloadURL,
      };
    }

    return uploadResults;
  } catch (error) {
    console.error("Error uploading documents:", error);
    throw new Error(
      "An error occurred while uploading documents. Please retry."
    );
  }
};
// ACTION: Upload Document to firebase
export const uploadDocumentToFirebase = async (
  fileType,
  file,
  company,
  collectionBrand,
  systemName,
  uniqueId,
  fileName,
  setLoadingLabel
) => {
  try {
    // Set the loading label for feedback

    const filePath = `${company}/${collectionBrand}/${systemName}/${uniqueId}/${fileName}`;
    const downloadURL = await uploadDocument(
      fileType,
      file,
      company,
      collectionBrand,
      systemName,
      uniqueId,
      fileName
    );

    // Example values of expected return:
    // filePath: 'AndUs/FinanceSystem/SupplierData/abc123/proofOfBankAccount.pdf'
    // downloadURL: 'https://firebasestorage.googleapis.com/.../proofOfBankAccount.pdf'

    return { filePath, downloadURL };
  } catch (error) {
    console.error("Error uploading document to Firebase Storage:", error);
    throw new Error(`Error uploading document ${fileName}. Please try again.`);
  }
};

// --------------------------------STEP 2---------------------------------
// HELPER: Extract text from the uploaded document
export const handleStep2ExtractText = async (
  uploadResults,
  uploadedFiles,
  setLoadingLabel,
  userEmail,
  systemName,
  company,
  collectionBrand,
  documentSupportData,
  totalDocuments
) => {
  const textExtractionResults = {};

  try {
    let docIndex = 0;
    const percentagePerDoc = 30 / totalDocuments;

    for (const [fileName, { filePath }] of Object.entries(uploadResults)) {
      docIndex++;
      const docCurrentPercent = (percentagePerDoc * docIndex).toFixed(2);
      console.log("step 2 docCurrentPercent", docCurrentPercent);
      const stepPercent = 20 + Number(docCurrentPercent);
      console.log("step 2 docCurrentPercent + 5", stepPercent);
      // Retrieve the original key without the extension
      const fileType = uploadedFiles[fileName].fileType;

      const { extractedText, isOCR } = await extractTextFromDocument(
        filePath,
        setLoadingLabel,
        fileName,
        userEmail,
        systemName,
        company,
        collectionBrand,
        documentSupportData,
        fileType,
        stepPercent
      );
      // Save the extracted text and isOCR flag for each uploaded document

      textExtractionResults[fileName] = {
        extractedText,
        isOCR,
      };
    }

    return textExtractionResults;
  } catch (error) {
    console.error("Error extracting text from documents:", error);
    throw new Error("An error occurred while extracting text. Please retry.");
  }
};

// ACTION: Extract text from the uploaded document
/**
 * Extracts text or data from a document based on its file type.
 *
 * @param {string} filePath - The path to the file in Firebase Storage.
 * @param {Function} setLoadingLabel - Function to update the loading label in the UI.
 * @param {string} fileName - The name of the file.
 * @param {string} userEmail - The email of the user performing the operation.
 * @param {string} systemName - The name of the system.
 * @param {string} company - The company name.
 * @param {string} collectionBrand - The collection brand.
 * @param {Object} documentSupportData - Additional data supporting the document.
 * @param {string} fileType - The type of the file (e.g., 'pdf', 'csv', 'jpg').
 * @returns {Object} - Contains the extracted text/data and an isOCR flag.
 */
export const extractTextFromDocument = async (
  filePath,
  setLoadingLabel,
  fileName,
  userEmail,
  systemName,
  company,
  collectionBrand,
  documentSupportData,
  fileType,
  stepPercent
) => {
  try {
    // Define supported image file extensions
    const imageFileTypes = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "bmp",
      "svg",
      "webp",
      "tiff",
      "tif",
      "heic",
      "heif",
      "raw",
      "ico",
      "jfif",
      "pjpeg",
      "pjp",
      "apng",
      "avif",
      "svgz",
      "xbm",
      "dib",
      "jpe",
    ];

    // Define supported Word file extensions
    const wordFileTypes = ["docx"];

    // Handle PDF files
    if (fileType.toLowerCase() === "pdf") {
      setLoadingLabel(`${stepPercent}% ....Extracting text from ${fileName}`);

      // Call the PDF extraction Cloud Function
      const extractTextFromPDF = httpsCallable(functions, "extractTextFromPDF");
      const pdfResponse = await extractTextFromPDF({
        data: {
          bucketName: "andus-education.appspot.com", // Replace with your actual bucket name
          fileName: filePath,
        },
      });

      const { text: extractedText, isOCR } = pdfResponse.data;

      if (isOCR) {
        // Handle OCR logic if needed
        const combinedStructuredHtml = await handleOCR(
          userEmail,
          systemName,
          company,
          collectionBrand,
          isOCR,
          filePath,
          setLoadingLabel,
          fileName,
          functions,
          documentSupportData,
          getPDFPageCount,
          createStructuredDocumentFromImage,
          logTokenUsage,
          buildCombinedDocument,
          stepPercent
        );

        return { extractedText: combinedStructuredHtml, isOCR };
      } else {
        return { extractedText, isOCR };
      }
    }
    // Handle Image files
    else if (imageFileTypes.includes(fileType.toLowerCase())) {
      setLoadingLabel(`${stepPercent}% ....Processing image file ${fileName}`);

      // Call the processImage Cloud Function to get a signed URL
      const processImage = httpsCallable(functions, "processImage");
      const response = await processImage({
        bucketName: "andus-education.appspot.com", // Replace with your actual bucket name if different
        fileName: filePath,
      });

      // Extract the signed URL from the response
      const { url: signedUrl } = response.data;

      const documentData = documentSupportData[fileName];
      const docLabel = documentData?.label || "Unknown Label";
      const docDescription =
        documentData?.description || "No Description Available";

      // Create the AI request body using the signed URL
      const openAIRequestBody = await createStructuredDocumentFromImage(
        signedUrl, // Pass the signed URL directly
        fileName, // Use the provided fileName
        docLabel,
        docDescription
      );

      // Call the AI via the OpenAIapiCalls Cloud Function
      const OpenAIapiCalls = heroAiCallable(functions, "OpenAIReasoningUpdate", {
        timeout: 3_600_000,
      });
      const AIResponse = await OpenAIapiCalls({ data: openAIRequestBody });

      // Log the token usage for tracking purposes
      await logTokenUsage({
        usage: AIResponse.data.usage,
        userID: userEmail, // Add userID
        systemName, // Add systemName
        action: `Extracting structured text from image ${fileName}.`, // Keep action
        company, // Add company
        collectionBrand, // Add collectionBrand
      });

      // Extract the structured HTML content from the AI response
      const structuredHTML = AIResponse.data.choices[0].message.content;
      console.log("----------STRUCTURED HTML--------");
      console.log(fileName, "HTML OUTPUT", structuredHTML);
      console.log("----------STOP--------");

      // Call the Firestore Operations Cloud Function to write data
      const firestoreOperations = httpsCallable(
        functions,
        "firestoreOperations"
      );

      // Write the structured HTML and image path to Firestore
      await firestoreOperations({
        data: {
          operation: "write",
          company: company,
          collectionBrand: collectionBrand,
          systemName: systemName,
          uniqueId: fileName, // Use fileName as uniqueId
          documentData: {
            structuredHtml: structuredHTML,
            imagePath: filePath,
          },
        },
      });

      // Return the structured HTML
      return { extractedText: structuredHTML, isOCR: true };
    }
    // Handle Word documents
    else if (wordFileTypes.includes(fileType.toLowerCase())) {
      console.log("WORD FILE DETECTED");
      setLoadingLabel(
        `${stepPercent}% ....Extracting text from Word document ${fileName}`
      );

      // Call the extractTextFromWord Cloud Function
      const extractTextFromWord = httpsCallable(
        functions,
        "extractTextFromWord"
      );
      const wordResponse = await extractTextFromWord({
        data: {
          bucketName: "andus-education.appspot.com", // Replace with your actual bucket name
          fileName: filePath,
        },
      });

      const { extractedText, extractedHtml, success, message } =
        wordResponse.data;

      if (!success) {
        console.error("Word extraction failed:", message);
        throw new Error(`Word extraction failed: ${message}`);
      }

      // Return the extracted data
      return {
        extractedText: extractedText,
        isOCR: false,
      };
    }
    // Handle Unsupported file types (including CSV)
    else {
      throw new Error(`Unsupported file type: .${fileType}`);
    }
  } catch (error) {
    console.error("Error extracting text/data from the document:", error);
    // Throw an error if there is an issue during extraction
    throw new Error(
      `Error extracting text/data from document. Please try again. ${error.message}`
    );
  }
};

// -----Support-----
/**
 * Handles the OCR process for a document.
 *
 * @param {string} filePath - The path to the PDF file in Firebase Storage.
 * @param {Function} setLoadingLabel - Function to update the loading label in the UI.
 * @param {string} fileName - The name of the PDF file.
 * @param {Object} functions - Firebase functions instance for calling cloud functions.
 * @param {Object} documentSupportData - Additional data supporting the document.
 * @param {Function} getPDFPageCount - Function to get the total number of pages in the PDF.
 * @param {Function} createStructuredDocumentFromImage - Function to create AI request body.
 * @param {Function} logTokenUsage - Function to log token usage.
 * @param {Function} buildCombinedDocument - Function to combine structured HTML responses.
 * @returns {Object} - Contains the combined structured HTML and isOCR flag.
 */
const handleOCR = async (
  userEmail,
  systemName,
  company,
  collectionBrand,
  isOCR,
  filePath,
  setLoadingLabel,
  fileName,
  functions,
  documentSupportData,
  getPDFPageCount,
  createStructuredDocumentFromImage,
  logTokenUsage,
  buildCombinedDocument,
  stepPercent
) => {
  // Initiate OCR loading label
  setLoadingLabel(
    `${stepPercent}% ....OCR process for ${fileName} initiated. This might take time please be patient`
  );

  // Get PDF page count
  const totalPages = await getPDFPageCount(filePath);

  // Determine chunk size based on total pages
  let chunkSize;
  if (totalPages > 200) {
    chunkSize = 30; // Smaller chunks for very large documents
  } else if (totalPages > 100) {
    chunkSize = 20; // Moderate chunk size
  } else if (totalPages > 20) {
    chunkSize = 10; // Larger chunk size for smaller documents
  } else {
    chunkSize = totalPages; // No chunking needed
  }

  // Define the split into chunks cloud function
  const splitIntoChunksFunction = httpsCallable(
    functions,
    "splitPDFIntoChunks"
  );

  const timeEstimate = totalPages / 2;

  // Define split into page cloud function
  const splitPDFIntoPages = httpsCallable(functions, "splitPDFIntoPages");
  const splitPageDirectories = []; // Initialize the array
  const chunkData = []; // Array to store data per chunk
  const allDestinationPaths = []; // Master list of all destinationPaths
  const allFileNames = []; // Master list of all fileNames

  // Split document into chunks and pages
  if (totalPages > 20) {
    setLoadingLabel(
      `${stepPercent}% ....This document has ${totalPages} pages. OCR will take approximately ${timeEstimate} minutes. Do not close your browser`
    );
    const response = await splitIntoChunksFunction({
      data: {
        bucketName: "andus-education.appspot.com",
        fileName: filePath,
        chunkSize: chunkSize,
      },
    });
    // We need to chunkFileNames to split individual chunks to single pages
    const chunkFileNames = response.data.chunkFileNames;
    // We need to split each chunk to single pages
    setLoadingLabel(
      `${stepPercent}% ....Making special preparations for OCR. OCR will take approximately ${timeEstimate} minutes. Do not close your browser`
    );

    for (const chunkFileName of chunkFileNames) {
      const response = await splitPDFIntoPages({
        data: {
          bucketName: "andus-education.appspot.com",
          fileName: chunkFileName,
        },
      });
      // Now we have the outputDir for all our pages
      const outputDir = response.data.outputDir;
      splitPageDirectories.push(outputDir);
      // Get the destinationPaths and fileNames
      const destinationPaths = response.data.destinationPaths;
      const fileNames = response.data.fileNames;
      // Store data per chunk
      chunkData.push({
        chunkFileName: chunkFileName,
        outputDir: outputDir,
        destinationPaths: destinationPaths,
        fileNames: fileNames,
      });
      // Append to master lists
      allDestinationPaths.push(...destinationPaths);
      allFileNames.push(...fileNames);
    }
  } else {
    // Documents smaller than 20 pages we split into single page directly
    setLoadingLabel(
      `${stepPercent}% ....The document is smaller than 20 pages. Preparing document for OCR`
    );
    const response = await splitPDFIntoPages({
      data: {
        bucketName: "andus-education.appspot.com",
        fileName: filePath,
      },
    });
    const outputDir = response.data.outputDir;
    splitPageDirectories.push(outputDir);

    const destinationPaths = response.data.destinationPaths;
    const fileNames = response.data.fileNames;

    // Store data per chunk (in this case, the whole document)
    chunkData.push({
      chunkFileName: filePath,
      outputDir: outputDir,
      destinationPaths: destinationPaths,
      fileNames: fileNames,
    });

    // Append to master lists
    allDestinationPaths.push(...destinationPaths);
    allFileNames.push(...fileNames);
  }

  setLoadingLabel(
    `${stepPercent}% ....Document preparations complete. Currently enhancing individual images for OCR`
  );
  // After the loop
  // Now you have:
  // - chunkData: array containing data per chunk
  // - allDestinationPaths: master list of all destinationPaths
  // - allFileNames: master list of all fileNames

  // Convert each single-page PDF to an image
  const convertPDFToImagesTest = httpsCallable(
    functions,
    "convertPDFToImagesFix"
  );
  // Initialize a master list to store all image paths
  const allImageDestinationPaths = [];

  if (totalPages === 1) {
    // Single-page PDF - process without looping
    const singlePagePDFPath = allDestinationPaths[0];
    const convertResponse = await convertPDFToImagesTest({
      data: {
        bucketName: "andus-education.appspot.com",
        fileName: singlePagePDFPath,
      },
    });

    let imagePaths = [];

    if (
      convertResponse.data.imageDestinationPaths &&
      Array.isArray(convertResponse.data.imageDestinationPaths)
    ) {
      // For multi-page PDFs or functions that return an array
      imagePaths = convertResponse.data.imageDestinationPaths;
    } else if (convertResponse.data.imageDestinationPath) {
      // For single-page PDFs
      imagePaths = [convertResponse.data.imageDestinationPath];
    } else {
      console.error(
        "No image destination paths returned for",
        singlePagePDFPath
      );
      throw new Error(
        `No image destination paths returned for ${singlePagePDFPath}`
      );
    }
    // Append to the master list
    allImageDestinationPaths.push(...imagePaths);
  } else {
    // Multi-page PDFs - loop through allDestinationPaths
    for (const singlePagePDFPath of allDestinationPaths) {
      const convertResponse = await convertPDFToImagesTest({
        data: {
          bucketName: "andus-education.appspot.com",
          fileName: singlePagePDFPath,
        },
      });

      // Extract image destination paths
      let imagePaths = [];

      if (
        convertResponse.data.imageDestinationPaths &&
        Array.isArray(convertResponse.data.imageDestinationPaths)
      ) {
        // For multi-page PDFs or functions that return an array
        imagePaths = convertResponse.data.imageDestinationPaths;
      } else if (convertResponse.data.imageDestinationPath) {
        // For single-page PDFs
        imagePaths = [convertResponse.data.imageDestinationPath];
      } else {
        console.error(
          "No image destination paths returned for",
          singlePagePDFPath
        );
        throw new Error(
          `No image destination paths returned for ${singlePagePDFPath}`
        );
      }

      // Append to the master list
      allImageDestinationPaths.push(...imagePaths);
    }
  }
  // Now you have allImageDestinationPaths containing all image paths;
  setLoadingLabel(
    `${stepPercent}% ....Images enhanced. Starting OCR process. Do not interrupt the process. This might take a few minutes. Please remain patient`
  );
  // Process each image

  const openAIapiCalls = heroAiCallable(functions, "OpenAIReasoningUpdate", {
    timeout: 3_600_000,
  });

  const firestoreOperations = httpsCallable(functions, "firestoreOperations");
  // Extract document-specific data from documentSupportData
  const documentData = documentSupportData[fileName];

  // Assign values to new variables with 'doc' prefix
  const docLabel = documentData?.label || "Unknown Label";
  const docDescription =
    documentData?.description || "No Description Available";

  const allResponses = []; // Array to store structured HTML responses
  const totalPagesNumber = allImageDestinationPaths.length;
  let currentPage = 1;

  for (const imagePath of allImageDestinationPaths) {
    setLoadingLabel(
      `${stepPercent}% ....${fileName}: Processing page ${currentPage} of ${totalPagesNumber}`
    );

    // Call the Cloud Function to get the signed URL
    const processImage = httpsCallable(functions, "processImage");
    const response = await processImage({
      bucketName: "andus-education.appspot.com",
      fileName: imagePath,
    });

    // Extract the signed URL
    const { url: signedUrl } = response.data;
    // Get the file name from the image path
    const imageFileName = imagePath.split("/").pop();

    // Create the AI request body using the signed URL
    const openAIRequestBody = await createStructuredDocumentFromImage(
      signedUrl, // Pass the signed URL directly
      imageFileName,
      docLabel,
      docDescription
    );

    // Call the OpenAI API using the cloud function
    const AIResponse = await openAIapiCalls({ data: openAIRequestBody });
    // Log the token usage
    await logTokenUsage({
      usage: AIResponse.data.usage,
      userID: userEmail,
      systemName,
      action: `Extracting structured text from image ${imageFileName}.`,
      company,
      collectionBrand,
    });

    // Get the structured HTML content
    const structuredHtml = AIResponse.data.choices[0].message.content;

    // Write the response to Firestore
    await firestoreOperations({
      data: {
        operation: "write",
        company: company,
        collectionBrand: collectionBrand,
        systemName: "ocrPages",
        uniqueId: imageFileName, // Use image name as uniqueId
        documentData: {
          structuredHtml: structuredHtml,
          imagePath: imagePath,
        },
      },
    });

    // Collect all structuredHtml responses
    allResponses.push(structuredHtml);
    // Increment the current page counter
    currentPage++;
  }

  // After the loop that processes each image and populates allResponses
  const combinedStructuredHtml = buildCombinedDocument(allResponses);
  console.log("FINAL COMBINED DOCUMENT", combinedStructuredHtml);
  // Return the combined structured HTML and isOCR flag
  return combinedStructuredHtml;
};

const buildCombinedDocument = (allResponses) => {
  /**
   * Builds a combined HTML document from individual page responses.
   *
   * @param {Array<string>} allResponses - Array of HTML strings, each containing a <Page>...</Page> structure.
   * @returns {string} - A single HTML string wrapped in <Document> tags with ordered pages.
   */
  // Initialize the Document with opening tag
  let combinedDocument = "<Document>\n";

  allResponses.forEach((response, index) => {
    // Regular expression to extract content within <Page> tags
    const pageContentMatch = response.match(/<Page>([\s\S]*?)<\/Page>/i);

    if (pageContentMatch && pageContentMatch[1]) {
      const pageContent = pageContentMatch[1].trim();
      combinedDocument += `  <Page>\n    ${pageContent}\n  </Page>\n`;
    } else {
      console.warn(`Page ${index + 1} does not contain valid <Page> tags.`);
      // Optionally, handle the missing <Page> tags as needed
    }
  });

  // Close the Document tag
  combinedDocument += "</Document>";
  return combinedDocument;
};

// SUPPORT FUNCTION STEP 2: Function to get page count
const getPDFPageCount = async (filePath) => {
  const getPageCountFunction = httpsCallable(functions, "getPDFPageCount");
  const response = await getPageCountFunction({
    bucketName: "andus-education.appspot.com",
    fileName: filePath,
  });
  return response.data.totalPages;
};

// --------------------------------STEP 3---------------------------------
// HELPER 3: Reformat extracted text using AI
export const handleStep3ReformatText = async (
  textExtractionResults,
  isOCRDocument,
  userEmail,
  systemName,
  company,
  collectionBrand,
  setLoadingLabel,
  documentSupportData,
  uploadedFiles,
  totalDocuments
) => {
  const reformattedTextResults = {};

  try {
    // Determine Percentage Progress
    let docIndex = 0;
    const percentagePerDoc = 10 / totalDocuments;
    for (const [fileName, extractedText] of Object.entries(
      textExtractionResults
    )) {
      // Percentage tracker progress bar
      docIndex++;
      const docCurrentPercent = (percentagePerDoc * docIndex).toFixed(2);
      const stepPercent = 50 + Number(docCurrentPercent);

      // Determine if the document was processed using OCR
      const isOCR = isOCRDocument[fileName];
      // Retrieve the original key without the extension
      const fileType = uploadedFiles[fileName].fileType;

      // Extract document-specific data from documentSupportData
      const documentData = documentSupportData[fileName];

      // Assign values to new variables with 'doc' prefix
      const docLabel = documentData?.label || "Unknown Label";
      const docDescription =
        documentData?.description || "No Description Available";

      if (!documentData) {
        console.warn(
          `No document data found for ${fileName}. Using default values.`
        );
      }

      // Call the reformatTextUsingAI action function for each document
      const formattedText = await reformatTextUsingAI(
        extractedText,
        userEmail,
        systemName,
        fileName,
        company,
        collectionBrand,
        setLoadingLabel,
        isOCR,
        docLabel,
        docDescription,
        fileType,
        stepPercent
      );

      // Save the formatted text for each document
      reformattedTextResults[fileName] = formattedText;
    }

    return reformattedTextResults;
  } catch (error) {
    console.error("Error reformatting text for documents:", error);
    throw new Error("An error occurred while reformatting text. Please retry.");
  }
};

// ACTION 3: Reformat extracted text using AI
export const reformatTextUsingAI = async (
  extractedText,
  userEmail,
  systemName,
  fileName,
  company,
  collectionBrand,
  setLoadingLabel,
  isOCR,
  docLabel,
  docDescription,
  fileType,
  stepPercent
) => {
  try {
    setLoadingLabel(
      `${stepPercent}% ....Reformatting text for ${fileName} to improve readability`
    );

    const OpenAIapiCalls = heroAiCallable(functions, "OpenAIReasoningUpdate", {
      timeout: 3_600_000,
    });
    let formatTextRequestBody;

    // Check if the document was processed using OCR
    if (isOCR) {
      // If it's an OCR document, use the OCR-specific prompt
      formatTextRequestBody = await reformatOCRDocument(
        extractedText,
        fileName,
        docLabel,
        docDescription
      );
    } else if (fileType === "pdf") {
      // Normal function if it was a standard pdf extract
      formatTextRequestBody = await reformatDocument(extractedText);
    } else if (fileType === "csv") {
      console.log("csv");
      console.log("extracted text", extractedText);
      // Use CSV-specific reformatting prompt
      formatTextRequestBody = await reformatCSVDocument(
        extractedText,
        fileName,
        docLabel,
        docDescription
      );
    } else {
      // Use a general reformatting prompt
      formatTextRequestBody = await reformatDocument(extractedText);
    }

    // Call OpenAI to get the formatted text
    const formattedTextResponse = await OpenAIapiCalls({
      data: formatTextRequestBody,
    });

    const formattedExtractedText =
      formattedTextResponse.data.choices[0].message.content;

    console.log("------------", fileName, "-------------");
    console.log("Original Text", extractedText);
    console.log("formatted Text:", formattedExtractedText);
    console.log("-------------------------");

    // Log token usage
    await logTokenUsage({
      usage: formattedTextResponse.data.usage,
      userID: userEmail,
      systemName,
      action: `Reformatting text for ${fileName}.`,
      company,
      collectionBrand,
    });

    return formattedExtractedText;
  } catch (error) {
    console.error("Error reformatting text using AI:", error);
    throw new Error(
      `Error reformatting text for ${fileName}. Please try again.`
    );
  }
};

// --------------------------------STEP 4---------------------------------
// HELPER 4: Extract data points from formatted text using AI
export const handleStep4ExtractDataPoints = async (
  reformattedTextResults,
  dataPointDefinitions,
  userEmail,
  systemName,
  company,
  collectionBrand,
  setLoadingLabel,
  documentSupportData,
  totalDocuments
) => {
  const extractedDataPointsResults = {};

  try {
    // Percentage progress tracker
    let docIndex = 0;
    const percentagePerDoc = 20 / totalDocuments;

    for (const [fileName, formattedExtractedText] of Object.entries(
      reformattedTextResults
    )) {
      docIndex++;
      const docCurrentPercent = (percentagePerDoc * docIndex).toFixed(2);
      const stepPercent = 60 + Number(docCurrentPercent);

      // Extract document-specific data from documentSupportData
      const documentData = documentSupportData[fileName];
      // Assign values to new variables with 'doc' prefix
      const docLabel = documentData?.label || "Unknown Label";
      const docDescription =
        documentData?.description || "No Description Available";
      const docRequired = documentData?.required || false;
      // Call the extractDataPointsFromText action function for each document
      const extractedJson = await extractDataPointsFromText(
        formattedExtractedText,
        fileName,
        systemName,
        dataPointDefinitions,
        userEmail,
        company,
        collectionBrand,
        setLoadingLabel,
        docLabel, // Pass the document label
        docDescription, // Pass the document description
        docRequired,
        stepPercent
      );

      // Save the extracted data points for each document
      extractedDataPointsResults[fileName] = extractedJson;
    }
    // Normalize the data
    const normalizedData = normalizeExtractedDataPoints(
      extractedDataPointsResults
    );

    return normalizedData;
  } catch (error) {
    console.error("Error extracting data points for documents:", error);
    throw new Error(
      "An error occurred while extracting data points. Please retry."
    );
  }
};

// STEP 4 ACTION FUNCTION
export const extractDataPointsFromText = async (
  formattedExtractedText,
  fileName,
  systemName,
  dataPointDefinitions,
  userEmail,
  company,
  collectionBrand,
  setLoadingLabel,
  docLabel, // New parameter for document label
  docDescription, // New parameter for document description
  docRequired,
  stepPercent
) => {
  try {
    setLoadingLabel(
      `${stepPercent}% ....Analyzing ${fileName} to extract data points`
    );

    const OpenAIapiCalls = heroAiCallable(functions, "OpenAIReasoningUpdate", {
      timeout: 3_600_000,
    });
    const openAIRequestBody = await extractDatapointsAI(
      formattedExtractedText,
      fileName,
      systemName,
      dataPointDefinitions,
      docLabel,
      docDescription,
      docRequired
    );
    const AIResponse = await OpenAIapiCalls({ data: openAIRequestBody });

    const textJson = AIResponse.data.choices[0].message.content;

    // Log token usage
    await logTokenUsage({
      usage: AIResponse.data.usage,
      userID: userEmail,
      systemName,
      action: `Extracting data points from ${fileName}.`,
      company,
      collectionBrand,
    });

    const extractedJson = extractJsonFromText(textJson);

    if (!extractedJson) {
      throw new Error("No valid JSON was found in the AI response.");
    }

    // Example value of expected return (JSON structure):
    // {
    //   "documentExtract": {
    //     "documentType": "Proof of Bank Account",
    //     "isproofOfBankAccount": true,
    //     "docOverview": "This document appears to be a valid proof of bank account for the company.",
    //     "riskScore": "Low",
    //     "riskElements": "None",
    //     "companyName": "BETTER BEST EDUCATIONAL PROJECTS (PTY) LTD",
    //     "accountNumber": "53510029333",
    //     "bankName": "First National Bank"
    //   }
    // }

    return extractedJson;
  } catch (error) {
    console.error("Error analyzing document content using AI:", error);
    throw new Error(
      `Error analyzing document for ${fileName}. Please try again.`
    );
  }
};

// STEP 4 SUPPORT FUNCTION - NORMALIZE JSON
const normalizeExtractedDataPoints = (extractedDataPointsResults) => {
  const normalizedResults = {};

  for (const [documentName, data] of Object.entries(
    extractedDataPointsResults
  )) {
    // Check if data is an array and if it has an object wrapped under the "0" key
    if (
      Array.isArray(data) &&
      data.length > 0 &&
      data[0] &&
      typeof data[0] === "object"
    ) {
      if ("0" in data[0]) {
        // Extract the object under the "0" key and replace it with the simplified structure
        normalizedResults[documentName] = data[0]["0"];
      } else if (data.length === 1) {
        // If there's only one object in the array, unwrap it
        normalizedResults[documentName] = data[0];
      } else {
        // Otherwise, keep it as an array of objects
        normalizedResults[documentName] = data;
      }
    } else {
      // If the data is already flat, leave it as-is
      normalizedResults[documentName] = data;
    }
  }

  return normalizedResults;
};

// --------------------------------STEP 4b---------------------------------
// HELPER 4b: Extract HTML structure with tags for datapoints
export const handleStep4bCreateStructuredHTML = async (
  extractedDataPointsResults,
  reformattedTextResults,
  isOCRdocument,
  userEmail,
  systemName,
  company,
  collectionBrand,
  setLoadingLabel,
  documentSupportData,
  uploadedFiles,
  totalDocuments
) => {
  const structuredHTMLResults = {};

  try {
    // Track percentage progress
    let docIndex = 0;
    const percentagePerDoc = 10 / totalDocuments;

    // Iterate through each document and generate structured HTML
    for (const [fileName, extractedDataPoints] of Object.entries(
      extractedDataPointsResults
    )) {
      // Percentage progress tracker
      docIndex++;
      const docCurrentPercent = (percentagePerDoc * docIndex).toFixed(2);
      const stepPercent = 80 + Number(docCurrentPercent);

      const fileType = uploadedFiles[fileName].fileType;
      // Determine if the document was processed using OCR
      const isOCR = isOCRdocument[fileName];
      // Extract document-specific data from documentSupportData
      const documentData = documentSupportData[fileName];

      // Assign values to new variables with 'doc' prefix
      const docLabel = documentData?.label || "Unknown Label";
      const docDescription =
        documentData?.description || "No Description Available";
      const docRequired = documentData?.required || false;
      // Get the formatted text for the current file
      const formattedText = reformattedTextResults[fileName];
      // Call the createStructuredHTMLDocument function for each document
      const structuredHTML = await createStructuredHTMLDocument(
        formattedText,
        extractedDataPoints,
        fileName,
        setLoadingLabel,
        userEmail,
        systemName,
        company,
        collectionBrand,
        docLabel, // Pass the document label
        docDescription, // Pass the document description
        docRequired, // Pass whether the document is required
        isOCR,
        fileType,
        stepPercent
      );

      // Store the structured HTML result for each document
      structuredHTMLResults[fileName] = structuredHTML;
    }

    // Example of expected structuredHTML:
    // {
    //   "fileName1": "<Document><header>Document Title</header><section><h1>Main Heading</h1><p>Some paragraph text...</p></section><div class=\"possible-table\">...</div></Document>",
    //   "fileName2": "<Document><header>Another Document Title</header><section><h1>Another Heading</h1><p>More paragraph text...</p></section></Document>"
    // }

    return structuredHTMLResults; // Return all structured HTML results
  } catch (error) {
    console.error("Error creating structured HTML for documents:", error);
    // Throw an error if there is an issue generating structured HTML
    throw new Error(
      "An error occurred while creating structured HTML. Please retry."
    );
  }
};

// ACTION 4b: Extract HTML structure with tags for datapoints
// ACTION 4b: Extract HTML structure with tags for datapoints
export const createStructuredHTMLDocument = async (
  formattedText,
  extractedDataPoints,
  fileName,
  setLoadingLabel,
  userEmail,
  systemName,
  company,
  collectionBrand,
  docLabel, // Add docLabel
  docDescription, // Add docDescription
  docRequired, // Add docRequired
  isOCR,
  fileType,
  stepPercent
) => {
  try {
    // Set loading label to indicate progress
    setLoadingLabel(
      `${stepPercent}% ....Creating structured HTML for ${fileName}`
    );

    // Prepare the OpenAI API request to create structured HTML
    const OpenAIapiCalls = heroAiCallable(functions, "OpenAIReasoningUpdate", {
      timeout: 3_600_000,
    });
    let openAIRequestBody;

    // Check if the document was processed using OCR
    // Determine which reformatting function to use based on document type
    if (isOCR) {
      // Handle OCR-processed documents
      openAIRequestBody = await createStructuredOCRDocument(
        formattedText,
        extractedDataPoints,
        fileName,
        docLabel,
        docDescription,
        docRequired
      );
    } else if (fileType.toLowerCase() === "pdf") {
      // Handle PDF documents
      openAIRequestBody = await createStructuredDocument(
        formattedText,
        extractedDataPoints,
        fileName,
        docLabel,
        docDescription,
        docRequired
      );
    } else if (fileType.toLowerCase() === "csv") {
      // Handle CSV documents
      console.log("CSV Request Body", formattedText);
      // Call the CSV extraction Cloud Function
      const extractTextFromCSV = httpsCallable(functions, "extractTextFromCSV");
      const csvResponse = await extractTextFromCSV({
        data: {
          bucketName: "andus-education.appspot.com",
          fileName: fileName,
          docLabel: docLabel,
          docDescription: docDescription,
          extractedDataPoints: extractedDataPoints,
        },
      });
      // You might want to handle the CSV response here if needed
    }
    // Handle Word documents
    else if (fileType.toLowerCase() === "docx") {
      // Handle Word documents
      openAIRequestBody = await createStructuredDocument(
        formattedText,
        extractedDataPoints,
        fileName,
        docLabel,
        docDescription,
        docRequired
      );
    } else {
      // Handle unsupported file types
      throw new Error(`Unsupported file type: .${fileType}`);
    }

    // Make the API call to OpenAI
    const AIResponse = await OpenAIapiCalls({ data: openAIRequestBody });

    // Log token usage for tracking purposes
    await logTokenUsage({
      usage: AIResponse.data.usage,
      userID: userEmail, // Add userID
      systemName, // Add systemName
      action: `Creating structured HTML for ${fileName}.`, // Keep action
      company, // Add company
      collectionBrand, // Add collectionBrand
    });

    // Extract the structured HTML content from the AI response
    const structuredHTML = AIResponse.data.choices[0].message.content;
    console.log("----------STRUCTURED HTML--------");
    console.log(fileName, "HTML OUTPUT", structuredHTML);
    console.log("----------STOP--------");

    return structuredHTML; // Return the structured HTML content
  } catch (error) {
    console.error("Error creating structured HTML using AI:", error);
    // Throw an error if there is an issue during HTML generation
    throw new Error(
      `Error creating structured HTML for ${fileName}. Please try again.`
    );
  }
};

// --------------------------------STEP 5---------------------------------
// HELPER 5: Update Firestore with document details
export const handleStep5UpdateFirestore = async (
  company,
  collectionBrand,
  systemName,
  uniqueId,
  uploadResults,
  reformattedTextResults,
  extractedDataPointsResults,
  structuredHTMLResults, // New parameter added to pass structured HTML
  setLoadingLabel,
  isOCR,
  totalDocuments
) => {
  try {
    // Track percentage
    let docIndex = 0;
    const percentagePerDoc = 5 / totalDocuments;
    for (const [fileName, { downloadURL }] of Object.entries(uploadResults)) {
      // Track percentage
      docIndex++;
      const docCurrentPercent = (percentagePerDoc * docIndex).toFixed(2);
      const stepPercent = 90 + Number(docCurrentPercent);
      // Use the action function to update Firestore with the document details
      await updateFirestoreWithDocument(
        company,
        collectionBrand,
        systemName,
        uniqueId,
        fileName,
        downloadURL,
        reformattedTextResults[fileName],
        extractedDataPointsResults[fileName],
        structuredHTMLResults[fileName], // Pass the corresponding structured HTML for each document
        setLoadingLabel,
        isOCR[fileName],
        stepPercent
      );
    }
  } catch (error) {
    console.error("Error updating Firestore for documents:", error);
    throw new Error(
      "An error occurred while updating Firestore. Please retry."
    );
  }
};

export const updateFirestoreWithDocument = async (
  company,
  collectionBrand,
  systemName,
  uniqueId,
  fileName,
  downloadURL,
  formattedExtractedText,
  extractedJson,
  htmlDocument, // New parameter for HTML document
  setLoadingLabel,
  isOCR,
  stepPercent
) => {
  try {
    setLoadingLabel(
      `${stepPercent}% ....Updating your database with details for ${fileName}`
    );

    const firestoreOperations = httpsCallable(functions, "firestoreOperations");
    await firestoreOperations({
      operation: "update",
      company,
      collectionBrand,
      systemName,
      uniqueId,
      documentData: {
        [`${fileName}Document`]: {
          url: downloadURL,
          electronicDocument: formattedExtractedText,
          auditResult: extractedJson.auditResult
            ? extractedJson.auditResult
            : {},
          htmlDocument: htmlDocument,
          isOCR: isOCR,
        },
      },
    });
  } catch (error) {
    console.error("Error updating Firestore:", error);
    throw new Error(
      `Error updating Firestore for ${fileName}. Please try again.`
    );
  }
};

// --------------------------------STEP 6---------------------------------
// HELPER 6: Consolidate Extracted Data Points
export const handleStep6ConsolidateDataPoints = async (
  extractedDataPointsResults,
  setLoadingLabel,
  uploadedFiles,
  totalDocuments
) => {
  try {
    // Set loading label to indicate the consolidation process
    setLoadingLabel(
      "97%...Consolidating extracted data points across all documents..."
    );

    // Call the action function to perform the consolidation
    const consolidatedDataPoints = await consolidateExtractedDataPointsAction(
      extractedDataPointsResults,
      uploadedFiles
    );

    return consolidatedDataPoints;
  } catch (error) {
    console.error("Error consolidating extracted data points:", error);
    throw new Error(
      "An error occurred while consolidating data points. Please retry."
    );
  }
};

// ACTION 6: Consolidate Extracted Data Points
export const consolidateExtractedDataPointsAction = async (
  extractedDataPointsResults,
  uploadedFiles
) => {
  const consolidatedDataPoints = {};

  // Extract file names from uploadedFiles and create "is{fileName}" keys
  const isFileKeys = Object.keys(uploadedFiles).map(
    (fileName) => `is${fileName}`
  );

  // List of keys to be excluded from consolidation
  const excludedKeys = [
    "documentType",
    "docOverview",
    "riskScore",
    "riskElements",
    "state",
    ...isFileKeys,
  ];

  // Iterate over each document in extractedDataPointsResults
  for (const [documentName, dataPoints] of Object.entries(
    extractedDataPointsResults
  )) {
    for (const [key, value] of Object.entries(dataPoints)) {
      // Skip keys that are related to audit data or other excluded fields
      if (excludedKeys.includes(key)) {
        continue;
      }

      // If the value is empty, skip it for conflict checking
      if (value === "") {
        continue;
      }

      if (!consolidatedDataPoints[key]) {
        // If the data point is not yet in consolidatedDataPoints, add it
        consolidatedDataPoints[key] = {
          value: value,
          documents: [documentName],
        };
      } else {
        // If a value already exists, check for conflicts
        if (
          consolidatedDataPoints[key].value !== value &&
          consolidatedDataPoints[key].value !== ""
        ) {
          // If the existing value and new value are different and neither is empty, mark as conflict
          if (consolidatedDataPoints[key].value !== "conflict") {
            // If it wasn't marked as a conflict before, convert it to conflict format
            consolidatedDataPoints[key] = {
              value: "conflict",
              documents: [
                {
                  value: consolidatedDataPoints[key].value,
                  sources: [consolidatedDataPoints[key].documents[0]],
                },
              ],
            };
          }
          // Add the new conflicting value and source
          consolidatedDataPoints[key].documents.push({
            value: value,
            sources: [documentName],
          });
        } else if (consolidatedDataPoints[key].value === value) {
          // If values match, add the document name to the list of sources
          consolidatedDataPoints[key].documents.push(documentName);
        } else if (consolidatedDataPoints[key].value === "") {
          // If the existing value is empty, update it with the new non-empty value
          consolidatedDataPoints[key].value = value;
          consolidatedDataPoints[key].documents = [documentName];
        }
      }
    }
  }

  return { consolidatedDataPoints };
};
// --------------------------------STEP 7---------------------------------
// HELPER 7: Resolve conflicting data points using AI
export const handleStep7ResolveConflicts = async (
  consolidatedData,
  systemName,
  userEmail,
  company,
  collectionBrand,
  setLoadingLabel,
  documentSupportData,
  totalDocuments
) => {
  /* ──────────────────────────────────────────────────────────────
     0.  “feature-flag”:  flip this to false if you want to bypass
         the o3-mini reasoning prompt and jump straight to the
         simpler fallback (gpt-4o-mini).
  ────────────────────────────────────────────────────────────── */
  const USE_EXPANDED_MODEL = false; //  << toggle here

  try {
    setLoadingLabel("98%… Resolving conflicting data points…");
    console.log("🟡 [Step 7] Starting conflict-resolver");

    /* ------------------------------------------------------------------ */
    /* 1. Build the primary request body (o3-mini with functions support) */
    /* ------------------------------------------------------------------ */
    const reqBodyV1 = await datapointConflictResolver(
      consolidatedData,
      systemName,
      documentSupportData
    );

    /* ------------------------------------------------------------------ */
    /* Option A: skip straight to fallback if flag is OFF                  */
    /* ------------------------------------------------------------------ */
    if (!USE_EXPANDED_MODEL) {
      console.log("⚠️  Expanded model disabled – jumping to fallback.");
      return await runSimpleFallback(
        reqBodyV1,
        consolidatedData,
        systemName,
        userEmail,
        company,
        collectionBrand
      );
    }

    /* ------------------------------------------------------------------ */
    /* 2. Call OpenAIReasoningUpdate (o3-mini)                                   */
    /* ------------------------------------------------------------------ */
    const OpenAIReasoningUpdate = heroAiCallable(
      functions,
      "OpenAIReasoningUpdate",
      {
        timeout: 3_600_000,
      }
    );

    console.log(
      "🟡 [Step 7] Hitting o3-mini … payload preview:",
      JSON.stringify(reqBodyV1).slice(0, 500),
      "…"
    );

    const res = await OpenAIReasoningUpdate({ data: reqBodyV1 });

    /* 2-a) Did it reply via function-calling?  */
    const fnArgs = res.data.choices?.[0]?.message?.function_call?.arguments;

    if (fnArgs) {
      console.log("🟢 [Step 7] Function-call JSON detected.");
      return JSON.parse(fnArgs); // already JSON, nothing to parse
    }

    /* 2-b) It’s plain text – try to scrape JSON */
    const txt = res.data.choices?.[0]?.message?.content ?? "";
    console.log("🟢 [Step 7] Raw text response:", txt.slice(0, 500), "…");

    const parsed = extractAuditJson(txt, consolidatedData);
    if (parsed) return parsed;

    console.warn("⚠️  JSON extraction failed – falling back.");
    return await runSimpleFallback(
      reqBodyV1,
      consolidatedData,
      systemName,
      userEmail,
      company,
      collectionBrand
    );
  } catch (err) {
    console.error("🔴 [Step 7] Expanded model failed:", err);
    /* Hard-fallback if anything explodes */
    return await runSimpleFallback(
      {}, // dummy, we re-create inside helper
      consolidatedData,
      systemName,
      userEmail,
      company,
      collectionBrand
    );
  }

  /* ──────────────────────────────────────────────────────────────
     Helper that executes the original “simple” prompt
     (gpt-4o-mini) and returns its parsed JSON.
  ────────────────────────────────────────────────────────────── */
  async function runSimpleFallback(
    _reqBodyV1,
    consolidatedData,
    systemName,
    userEmail,
    company,
    collectionBrand
  ) {
    const reqBodySimple = await datapointConflictResolverSimple(
      consolidatedData,
      systemName,
      documentSupportData
    );

    const OpenAIReasoningUpdate = heroAiCallable(
      functions,
      "OpenAIReasoningUpdate",
      {
        timeout: 3_600_000,
      }
    );

    console.log(
      "🟡 [Step 7] Simple fallback request:",
      JSON.stringify(reqBodySimple).slice(0, 500),
      "…"
    );

    const res = await OpenAIReasoningUpdate({ data: reqBodySimple });
    const txt = res.data.choices?.[0]?.message?.content ?? "";
    console.log("🟢 [Step 7] Simple raw text:", txt.slice(0, 500), "…");

    const parsed = extractAuditJson(txt, consolidatedData);
    if (parsed) return parsed;

    /* last resort – give the caller an empty object to avoid crash */
    console.error("🔴 [Step 7] Fallback JSON still failed – returning {}");
    return {};
  }
};

// Smart JSON extraction function for Step 7
export const extractAuditJson = (responseText) => {
  console.log("----JSON---");
  // Method 1: Check for START_JSON and END_JSON markers (case-insensitive)
  console.log("Attempting extraction using START_JSON and END_JSON markers...");
  const startMarkerIndex = responseText.search(/start_json/i);
  const endMarkerIndex = responseText.search(/end_json/i);

  if (startMarkerIndex !== -1 && endMarkerIndex !== -1) {
    const startIndex = responseText.indexOf("{", startMarkerIndex);
    const endIndex = responseText.lastIndexOf("}", endMarkerIndex);

    if (startIndex !== -1 && endIndex !== -1) {
      const jsonString = responseText.substring(startIndex, endIndex + 1);
      try {
        const parsedJson = JSON.parse(jsonString);
        console.log("Method 1: Extraction successful.");
        return parsedJson;
      } catch (e) {
        console.error("Method 1: JSON parsing failed:", e);
      }
    }
    console.log("Method 1: Extraction failed.");
  }

  // Method 2: Check for the name of the JSON (e.g., dataPoints or resolvedDataPoints)
  console.log(
    "Attempting extraction using the name of the JSON (dataPoints)..."
  );
  const dataPointsIndex = responseText.indexOf('"resolvedDataPoints":');
  if (dataPointsIndex !== -1) {
    const startIndex = responseText.indexOf("{", dataPointsIndex);
    const endIndex = responseText.lastIndexOf("}");

    if (startIndex !== -1 && endIndex !== -1) {
      const jsonString = responseText.substring(startIndex, endIndex + 1);
      try {
        const parsedJson = JSON.parse(jsonString);
        console.log("Method 2: Extraction successful.");
        return parsedJson;
      } catch (e) {
        console.error("Method 2: JSON parsing failed:", e);
      }
    }
    console.log("Method 2: Extraction failed.");
  }

  // Method 3: Generic extraction using the existing extractJsonFromText function
  console.log(
    "Attempting extraction using the generic JSON extraction function..."
  );
  const extractedJsons = extractJsonFromText(responseText);
  if (extractedJsons) {
    for (const json of extractedJsons) {
      try {
        const parsedJson = JSON.parse(JSON.stringify(json));
        console.log("Method 3: Extraction successful.");
        return parsedJson;
      } catch (e) {
        console.error("Method 3: JSON parsing failed:", e);
      }
    }
    console.log("Method 3: Extraction failed for all extracted JSONs.");
  }

  // If all methods fail
  console.log("All extraction attempts failed. Returning null.");
  return null;
};

// --------------------------------STEP 8---------------------------------
// HELPER 8: Generate Audit Risk Profile and Update Firestore
export const handleStep8GenerateAuditRiskProfile = async (
  preprocessedResolvedDataPoints,
  preprocessedAuditData,
  systemName,
  userEmail,
  company,
  collectionBrand,
  uniqueId,
  setLoadingLabel,
  documentSupportData
) => {
  try {
    // Set the loading label for feedback
    setLoadingLabel("Finishing up.. Generating audit risk profile...");

    // Generate the request body to generate the audit risk profile
    const RequestBodyAuditRiskProfile = await generateAuditRiskProfile(
      preprocessedResolvedDataPoints,
      preprocessedAuditData,
      systemName,
      documentSupportData
    );

    const OpenAIapiCalls = heroAiCallable(functions, "OpenAIReasoningUpdate", {
      timeout: 3_600_000,
    });

    // Make a call to OpenAI to generate the audit risk profile
    const AIAuditRiskProfileResponse = await OpenAIapiCalls({
      data: RequestBodyAuditRiskProfile,
    });

    // Extract the response content
    const textJson = AIAuditRiskProfileResponse.data.choices[0].message.content;

    // Log token usage for generating audit risk profile
    await logTokenUsage({
      usage: AIAuditRiskProfileResponse.data.usage,
      userID: userEmail,
      systemName,
      action: "Generating audit risk profile using AI",
      company,
      collectionBrand,
    });

    // Extract JSON from AI response
    const auditRiskProfileJson = extractAuditRiskProfileJson(textJson);

    // If the AI response does not return valid JSON, throw an error
    if (!auditRiskProfileJson) {
      throw new Error(
        "No valid JSON was found in the AI response for audit risk profile."
      );
    }

    // Set the loading label for updating Firestore
    setLoadingLabel("Updating Firestore with audit data...");

    // Write the resolved data points and audit risk profile to Firestore
    const firestoreOperations = httpsCallable(functions, "firestoreOperations");
    await firestoreOperations({
      operation: "update",
      company,
      collectionBrand,
      systemName,
      uniqueId,
      documentData: {
        extractedData: preprocessedResolvedDataPoints, // Write the final audit report to Firestore
        auditRiskProfile: auditRiskProfileJson.auditRiskProfile, // Write the audit risk profile to Firestore
      },
    });

    return {
      auditRiskProfileJson,
    };
  } catch (error) {
    console.error("Error generating audit risk profile using AI:", error);
    throw new Error(
      "An error occurred while generating the audit risk profile. Please retry."
    );
  } finally {
    // Clear the loading label after completion or failure
    setLoadingLabel("");
  }
};

// Smart JSON extraction function for Step 8 (Audit Risk Profile)
export const extractAuditRiskProfileJson = (responseText) => {
  const expectedFields = [
    "auditStatus",
    "auditOverview",
    "riskScore",
    "riskElements",
  ];

  // Function to validate JSON
  const isJsonValid = (extractedJson) => {
    try {
      const parsedJson = JSON.parse(extractedJson);

      // Check if all expected fields are present in the parsed JSON
      const parsedFields = Object.keys(parsedJson.auditRiskProfile || {});
      const missingFields = expectedFields.filter(
        (field) => !parsedFields.includes(field)
      );

      if (missingFields.length === 0) {
        return parsedJson;
      } else {
        console.log(
          "Missing fields in the extracted Audit Risk Profile JSON:",
          missingFields
        );
        return null;
      }
    } catch (error) {
      console.error(
        "Error parsing extracted JSON for Audit Risk Profile:",
        error
      );
      return null;
    }
  };

  // Method 1: Check for START_JSON and END_JSON markers (case-insensitive)
  const startMarkerIndex = responseText.search(/start_json/i);
  const endMarkerIndex = responseText.search(/end_json/i);

  if (startMarkerIndex !== -1 && endMarkerIndex !== -1) {
    const startIndex = responseText.indexOf("{", startMarkerIndex);
    const endIndex = responseText.lastIndexOf("}", endMarkerIndex);

    if (startIndex !== -1 && endIndex !== -1) {
      const jsonString = responseText.substring(startIndex, endIndex + 1);
      const parsedJson = isJsonValid(jsonString);
      if (parsedJson) {
        console.log("Method 1: Extraction and validation successful.");
        return parsedJson;
      }
    }
    console.log("Method 1: Extraction or validation failed.");
  }

  // Method 2: Check for the name of the JSON (auditRiskProfile)
  const dataPointsIndex = responseText.indexOf('"auditRiskProfile":');
  if (dataPointsIndex !== -1) {
    const startIndex = responseText.indexOf("{", dataPointsIndex);
    const endIndex = responseText.lastIndexOf("}");

    if (startIndex !== -1 && endIndex !== -1) {
      const jsonString = responseText.substring(startIndex, endIndex + 1);
      const parsedJson = isJsonValid(jsonString);
      if (parsedJson) {
        console.log("Method 2: Extraction and validation successful.");
        return parsedJson;
      }
    }
    console.log("Method 2: Extraction or validation failed.");
  }

  // Method 3: Generic extraction using the existing extractJsonFromText function
  const extractedJsons = extractJsonFromText(responseText);
  if (extractedJsons) {
    for (const json of extractedJsons) {
      const parsedJson = isJsonValid(JSON.stringify(json));
      if (parsedJson) {
        console.log("Method 3: Extraction and validation successful.");
        return parsedJson;
      }
    }
    console.log(
      "Method 3: Extraction or validation failed for all extracted JSONs."
    );
  }

  // If all methods fail
  console.log(
    "All extraction attempts failed for Audit Risk Profile JSON. Returning null."
  );
  return null;
};

// Function to handle document uploads, text extraction, and OpenAI analysis
export const handleDocumentUploadNext = async (
  company,
  collectionBrand,
  systemName,
  uploadedFiles,
  uniqueId,
  setLoadingLabel,
  setResult,
  dataPointDefinitions,
  userEmail
) => {
  try {
    // Create an array to store extracted data points for each document
    const extractedDataPoints = [];
    // Create objects to store consolidated data
    const consolidatedAuditData = {};
    const consolidatedExtractedDatapoints = {};

    // Iterate through each uploaded file
    // --------------------------------------START------------------------------------

    for (const [fileName, { file, label }] of Object.entries(uploadedFiles)) {
      // ---STEP 1----Upload the document to Firebase Storage
      let filePath;
      let downloadURL;
      try {
        // Upload the document to Firebase Storage
        setLoadingLabel(`Working on ${label}: Uploading document...`);
        filePath = `${company}/${collectionBrand}/${systemName}/${uniqueId}/${fileName}`;
        downloadURL = await uploadDocument(
          file,
          company,
          collectionBrand,
          systemName,
          uniqueId,
          fileName
        );
      } catch (error) {
        console.error("Error uploading document to Firebase Storage:", error);
        setResult({
          type: "error",
          message: `Error uploading document ${fileName}. Please try again.`,
        });
        return; // Stop further processing if an error occurs here
      }

      // ---STEP 2----Extract text from the uploaded document
      let extractedText;
      try {
        // Extract text from the uploaded document
        setLoadingLabel(
          `Working on ${fileName}: Extracting text from the document...`
        );
        const extractTextFromPDF = httpsCallable(
          functions,
          "extractTextFromPDF"
        );
        const response = await extractTextFromPDF({
          data: {
            bucketName: "andus-education.appspot.com",
            fileName: filePath,
          },
        });
        extractedText = response.data.text;
      } catch (error) {
        console.error("Error extracting text from the document:", error);
        setResult({
          type: "error",
          message: `Error extracting text from ${fileName}. Please try again.`,
        });
        return; // Stop further processing if an error occurs here
      }
      // ---STEP 3----Reformat the text using AI
      let formattedExtractedText;
      const OpenAIapiCalls = httpsCallable(functions, "OpenAIReasoningUpdate", {
        timeout: 3_600_000,
      });
      try {
        // Reformat the text using AI
        setLoadingLabel(
          `Working on ${fileName}: We are fixing formatting errors and creating an electronic audit document...`
        );
        const formatTextRequestBody = await reformatDocument(extractedText);

        const formattedTextResponse = await OpenAIapiCalls({
          data: formatTextRequestBody,
        });

        formattedExtractedText =
          formattedTextResponse.data.choices[0].message.content;
        // Writting Token Usages for AI calls
        const tokenUsageTextFormat = formattedTextResponse.data.usage;
        // Call the logTokenUsage function to store token usage data
        await logTokenUsage({
          usage: tokenUsageTextFormat,
          userID: userEmail,
          systemName: systemName, // This is the system where the action took place
          action: `Fixing formatting errors in ${fileName} and generating an electronic version of the document.`,
          company: company,
          collectionBrand: collectionBrand,
        });
      } catch (error) {
        console.error("Error reformatting text using AI:", error);
        setResult({
          type: "error",
          message: `Error reformatting text for ${fileName}. Please try again.`,
        });
        return; // Stop further processing if an error occurs here
      }
      // ---STEP 4----Extract the DataPoints
      let extractedJson;
      try {
        // Analyze the document content using AI
        setLoadingLabel(`Working on ${fileName}: Analyzing document...`);
        const openAIRequestBody = await extractDatapointsAI(
          formattedExtractedText,
          label,
          fileName,
          systemName,
          dataPointDefinitions
        );

        const AIResponse = await OpenAIapiCalls({
          data: openAIRequestBody,
        });

        const textJson = AIResponse.data.choices[0].message.content;
        // Writting Token Usages for AI calls
        const tokenUsagetextJson = AIResponse.data.usage;
        // Call the logTokenUsage function to store token usage data
        await logTokenUsage({
          usage: tokenUsagetextJson,
          userID: userEmail,
          systemName: systemName, // This is the system where the action took place
          action: `Extracting datapoints from ${fileName}.`,
          company: company,
          collectionBrand: collectionBrand,
        });
        extractedJson = extractJsonFromText(textJson);

        if (!extractedJson) {
          console.error("No valid JSON was found in the AI response.");
        }
      } catch (error) {
        console.error("Error analyzing document content using AI:", error);
        setResult({
          type: "error",
          message: `Error analyzing document for ${fileName}. Please try again.`,
        });
        return; // Stop further processing if an error occurs here
      }
      // ---STEP 4----Create export objects and write document output to firestore
      if (extractedJson) {
        extractedDataPoints.push({
          fileName,
          data: extractedJson,
        });
        // Consolidate audit information
        const auditDataKeys = [
          "documentType",
          `is${fileName}`,
          "docOverview",
          "riskScore",
          "riskElements",
        ];
        consolidatedAuditData[fileName] = {};
        for (const key of auditDataKeys) {
          if (key in extractedJson) {
            consolidatedAuditData[fileName][key] = extractedJson[key];
            delete extractedJson[key]; // Remove audit data from extractedJson so only extracted data points remain
          }
        }

        // Consolidate extracted data points for comparison
        for (const key in extractedJson) {
          if (!consolidatedExtractedDatapoints[key]) {
            consolidatedExtractedDatapoints[key] = [];
          }
          consolidatedExtractedDatapoints[key].push({
            source: fileName,
            value: extractedJson[key],
          });
        }
        // Update Firestore with the reformatted text and document details, including audit data
        setLoadingLabel(
          `Working on ${fileName}: Updating database with reformatted document and audit data...`
        );
        const firestoreOperations = httpsCallable(
          functions,
          "firestoreOperations"
        );
        try {
          await firestoreOperations({
            operation: "update",
            company: company,
            collectionBrand: collectionBrand,
            systemName: systemName,
            uniqueId: uniqueId,
            documentData: {
              [`${fileName}Document`]: {
                url: downloadURL,
                electronicDocument: formattedExtractedText,
                auditResult: consolidatedAuditData[fileName], // Add audit data for this document
              },
            },
          });
        } catch (error) {
          console.error("Error updating Firestore:", error);
          setResult({
            type: "error",
            message: `Error updating Firestore for ${fileName}. Please try again.`,
          });
          return; // Stop further processing if an error occurs here
        }
      } else {
        // Update Firestore with the reformatted text and document details, excluding audit data
        setLoadingLabel(
          `Working on ${fileName}: Updating database with reformatted document and audit data...`
        );
        const firestoreOperations = httpsCallable(
          functions,
          "firestoreOperations"
        );
        try {
          await firestoreOperations({
            operation: "update",
            company: company,
            collectionBrand: collectionBrand,
            systemName: systemName,
            uniqueId: uniqueId,
            documentData: {
              [`${fileName}Document`]: {
                url: downloadURL,
                electronicDocument: formattedExtractedText,
              },
            },
          });
        } catch (error) {
          console.error("Error updating Firestore:", error);
          setResult({
            type: "error",
            message: `Error updating Firestore for ${fileName}. Please try again.`,
          });
          return; // Stop further processing if an error occurs here
        }
        console.error("No valid JSON was found in the AI response.");
      }
    }
    // --------------------------------------END------------------------------------
    // Consolidate data points from all documents
    setLoadingLabel("Upload and Analysis complete. Consolidating results...");
    const { consolidatedData, rejectedData } = consolidateExtractedDataPoints(
      consolidatedExtractedDatapoints,
      consolidatedAuditData
    );

    console.log(
      "Returning cleaned data after removing rejected document data points:revisedConsolidatedData and rejectedData",
      { consolidatedData, rejectedData }
    );

    return { consolidatedData, consolidatedAuditData, rejectedData };
  } catch (error) {
    console.error("Error during document upload or analysis:", error);
    setLoadingLabel("");
    setResult({
      type: "error",
      message: "Error uploading or processing documents. Please try again.",
    });
  }
};

const consolidateExtractedDataPoints = (
  consolidatedExtractedDatapoints,
  consolidatedAuditData
) => {
  const consolidatedData = {};
  const rejectedData = {};

  // Iterate over each key in consolidatedExtractedDatapoints
  for (const key in consolidatedExtractedDatapoints) {
    if (consolidatedExtractedDatapoints.hasOwnProperty(key)) {
      const dataPoints = consolidatedExtractedDatapoints[key];
      const uniqueValues = new Map();
      const rejectedValues = new Map();

      // Iterate over each data point for the current key
      for (const dataPoint of dataPoints) {
        const { value, source } = dataPoint;

        // Ignore empty string values
        if (value === "") continue;

        // Check if the source document passed the audit
        if (
          consolidatedAuditData[source] &&
          consolidatedAuditData[source].auditFailed === true
        ) {
          // Store rejected data point in rejectedValues
          if (rejectedValues.has(value)) {
            rejectedValues.get(value).push(source);
          } else {
            rejectedValues.set(value, [source]);
          }
          continue; // Skip adding to valid data points
        }

        // If value already exists in uniqueValues, add the source to its list
        if (uniqueValues.has(value)) {
          uniqueValues.get(value).push(source);
        } else {
          // If value doesn't exist, add it to uniqueValues with the source
          uniqueValues.set(value, [source]);
        }
      }

      // Determine consolidated data for valid documents
      if (uniqueValues.size === 1) {
        // Only one value exists, so take it and its sources
        const [[value, sources]] = uniqueValues.entries();
        consolidatedData[key] = {
          value: value,
          documents: sources,
        };
      } else if (uniqueValues.size > 1) {
        // Conflicting values, mark as conflict and list all values with their sources
        consolidatedData[key] = {
          value: "conflict",
          documents: [...uniqueValues.entries()].map(([value, sources]) => ({
            value,
            sources,
          })),
        };
      } else {
        // No non-empty values were found, keep it empty
        consolidatedData[key] = {
          value: "",
          documents: [],
        };
      }

      // Determine rejected data
      if (rejectedValues.size > 0) {
        rejectedData[key] = {
          value:
            rejectedValues.size === 1
              ? Array.from(rejectedValues.keys())[0]
              : "conflict",
          documents: [...rejectedValues.entries()].map(([value, sources]) => ({
            value,
            sources,
          })),
        };
      } else {
        // If no rejected data points, mark it as empty
        rejectedData[key] = {
          value: "",
          documents: [],
        };
      }
    }
  }

  return { consolidatedData, rejectedData };
};

// InputFormFunctions.js
// Utility function to flatten nested objects
const flattenFormData = (data, parentKey = "", result = {}) => {
  for (let key in data) {
    if (data.hasOwnProperty(key)) {
      const newKey = parentKey ? `${parentKey}_${key}` : key;
      const value = data[key];

      if (
        typeof value === "object" &&
        !Array.isArray(value) &&
        value !== null
      ) {
        // Recursively flatten nested objects
        flattenFormData(value, newKey, result);
      } else if (Array.isArray(value)) {
        // Convert arrays to comma-separated strings
        result[newKey] = value.join(", ");
      } else {
        result[newKey] = value || "N/A"; // Handle undefined or null values
      }
    }
  }
  return result;
};

// navigateNext.js or navigateNext Function File

export const navigateNext = (
  currentSectionIndex,
  totalSections,
  setCurrentSectionIndex,
  validationRules,
  formData,
  currentSection,
  setWarningLabels
) => {
  const fieldWarnings = {}; // Object to hold warnings per field

  // Perform validation for each field in the current section
  currentSection.fields.forEach((field) => {
    const fieldName = field.name;
    const fieldLabel = field.label;
    const value = formData[fieldName];
    const warnings = [];

    // Check if the field is required and empty
    if (field.required && (!value || value.trim() === "")) {
      warnings.push(`${fieldLabel} is required.`);
    }

    // Perform validations if the field has a value and validationRules are provided
    if (value) {
      if (validationRules[fieldName]) {
        const { validate, errorMessage } = validationRules[fieldName];
        if (!validate(value)) {
          warnings.push(errorMessage);
        }
      }
      // No else block for default validations to ensure reusability
    }

    // If there are warnings for the field, add them to the fieldWarnings object
    if (warnings.length > 0) {
      fieldWarnings[fieldName] = warnings;
    }
  });

  // If there are any warnings, set them and prevent navigation
  if (Object.keys(fieldWarnings).length > 0) {
    setWarningLabels(fieldWarnings); // Set the warnings per field
    return; // Prevent navigation
  }

  // Clear previous warnings if validation passes
  setWarningLabels({});

  // Navigate to next section if not the last
  if (currentSectionIndex < totalSections - 1) {
    setCurrentSectionIndex((prevIndex) => prevIndex + 1);
  } else {
    // Handle form submission if it's the last section
    console.log("THIS WILL NEVER HAPPEN I THINK");
  }
};

// Function to handle saving the current section data and navigating to the next section for normal inputs
export const handleNext = async (
  formData,
  company,
  collectionBrand,
  systemName,
  uniqueId,
  checkDuplicateFields, // New parameter for duplicate checks
  validationRules, // Validation rules parameter
  currentSection, // Current section parameter
  setLoadingLabel,
  setResult,
  onSuccess
) => {
  setLoadingLabel("Saving data...");
  try {
    console.log("Saving data to Firestore");
    // Reference to the collection in Firestore
    const collectionRef = collection(db, company, collectionBrand, systemName);
    const docRef = doc(collectionRef, uniqueId);

    // Fetch the existing document
    const existingDoc = await getDoc(docRef);
    let existingData = {};
    if (existingDoc.exists()) {
      existingData = existingDoc.data();
    }

    // Validation for email and contactNumber fields
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const contactNumberRegex = /^[0-9]+$/;

    // Extract field names for the current section
    const currentSectionFields = currentSection.fields.map(
      (field) => field.name
    );

    // Check each field in the current section for validation based on its name
    for (const name of currentSectionFields) {
      const value = formData[name];
      if (validationRules && validationRules[name]) {
        const { validate, errorMessage } = validationRules[name];
        if (!validate(value)) {
          throw new Error(errorMessage);
        }
      } else {
        if (name === "email" && value && !emailRegex.test(value)) {
          throw new Error("Please enter a valid email address.");
        }
        if (
          name === "contactNumber" &&
          value &&
          !contactNumberRegex.test(value)
        ) {
          throw new Error("Please enter a valid contact number.");
        }
      }
    }

    // Create queries to check for duplicate records based on provided fields
    const duplicateQueries = checkDuplicateFields.map((field) => {
      return query(collectionRef, where(field, "==", formData[field]));
    });

    // Run all duplicate queries concurrently
    const duplicateSnapshots = await Promise.all(
      duplicateQueries.map((q) => getDocs(q))
    );

    // Check if any duplicate records exist
    for (let i = 0; i < duplicateSnapshots.length; i++) {
      const snapshot = duplicateSnapshots[i];
      const fieldName = checkDuplicateFields[i];
      if (!snapshot.empty && existingData[fieldName] !== formData[fieldName]) {
        throw new Error(`A record with the same ${fieldName} already exists.`);
      }
    }

    // Add or update the data in Firestore using the provided unique ID
    const filteredData = { ...formData };
    Object.keys(filteredData).forEach((key) => {
      if (filteredData[key] === "uploaded") {
        delete filteredData[key];
      }
    });

    await setDoc(
      docRef,
      {
        ...existingData,
        ...filteredData,
        addedAt: existingData.addedAt || new Date(),
      },
      { merge: true }
    );

    // If the save was successful, call onSuccess callback
    setLoadingLabel("");
    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    console.error("Error saving data: ", error);
    setLoadingLabel("");
    setResult({
      type: "error",
      message: error.message || "Error saving data. Please try again.",
    });
  }
};

export const handleCancel = async (
  company,
  collectionBrand,
  systemName,
  uniqueId,
  setLoadingLabel,
  setResult
) => {
  setLoadingLabel("Cancelling process...");
  try {
    console.log("Cancelling add record process");
    // Reference to the document in Firestore
    const collectionRef = collection(db, company, collectionBrand, systemName);
    const docRef = doc(collectionRef, uniqueId);

    // Delete document from Firestore if it exists
    await deleteDoc(docRef);
    console.log("Document deleted from Firestore");

    // Delete the entire folder from Firebase Storage
    const folderRef = ref(
      storage,
      `${company}/${collectionBrand}/${systemName}/${uniqueId}`
    );
    const folderContents = await listAll(folderRef);
    for (const itemRef of folderContents.items) {
      await deleteObject(itemRef);
      console.log(`File deleted from Storage: ${itemRef.fullPath}`);
    }

    // Refresh the browser to take the user back to the console dashboard
    window.location.reload();
  } catch (error) {
    console.error("Error during cancellation: ", error);
    setResult({
      type: "error",
      message: "Error cancelling the process. Please try again.",
    });
  } finally {
    setLoadingLabel("");
  }
};

const extractJsonFromText = (text) => {
  try {
    const results = [];
    let firstOpen = text.indexOf("{");
    let firstSquareOpen = text.indexOf("[");

    // Determine which opening brace comes first (if any)
    while (firstOpen !== -1 || firstSquareOpen !== -1) {
      let firstClose, candidate;

      if (
        (firstOpen !== -1 &&
          firstSquareOpen !== -1 &&
          firstOpen < firstSquareOpen) ||
        (firstSquareOpen === -1 && firstOpen !== -1)
      ) {
        // We have an object-like structure
        firstClose = text.lastIndexOf("}");
        if (firstClose === -1 || firstClose <= firstOpen) {
          break; // No valid closing brace found
        }

        // Iterate through potential candidates
        while (firstClose > firstOpen) {
          candidate = text.substring(firstOpen, firstClose + 1);
          try {
            const parsedJson = JSON.parse(candidate);
            results.push(parsedJson);
            break; // JSON parsed successfully, move on to next candidate
          } catch (e) {
            // Move to the previous closing brace
            firstClose = text.substring(0, firstClose).lastIndexOf("}");
          }
        }

        // Move to the next opening brace
        firstOpen = text.indexOf("{", firstOpen + 1);
      } else {
        // We have an array-like structure
        firstClose = text.lastIndexOf("]");
        if (firstClose === -1 || firstClose <= firstSquareOpen) {
          break; // No valid closing bracket found
        }

        // Iterate through potential candidates
        while (firstClose > firstSquareOpen) {
          candidate = text.substring(firstSquareOpen, firstClose + 1);
          try {
            const parsedJson = JSON.parse(candidate);
            results.push(parsedJson);
            break; // JSON parsed successfully, move on to next candidate
          } catch (e) {
            // Move to the previous closing bracket
            firstClose = text.substring(0, firstClose).lastIndexOf("]");
          }
        }

        // Move to the next opening bracket
        firstSquareOpen = text.indexOf("[", firstSquareOpen + 1);
      }
    }

    if (results.length === 0) {
      throw new Error(
        "No valid JSON could be extracted from the provided text."
      );
    }

    return results; // Return all successfully parsed JSON objects
  } catch (error) {
    console.error("Error extracting JSON from text:", error);
    return null;
  }
};

// util: convert new {results:[…]} to legacy {dataPoints:{…}}
export function mapResultsToDataPoints(resolved) {
  // Already in old shape? Just return.
  if (resolved?.dataPoints && typeof resolved.dataPoints === "object") {
    return resolved;
  }

  if (!Array.isArray(resolved?.results)) return resolved; // nothing to do

  const obj = {};
  for (const item of resolved.results) {
    if (item.status === "resolved" && item.value !== undefined) {
      obj[item.fieldName] = item.value;
    } else if (item.status === "unresolved") {
      // leave empty so downstream audit UI still shows the conflict
      obj[item.fieldName] = "⟸ unresolved ⟹";
    }
  }
  return { dataPoints: obj };
}
